import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Layers } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { useSetTestAccount } from "./use-set-test-account";
import {
  blockingEntities,
  isBlockingEntitiesError,
  resolveTestAccountError,
} from "./test-account-error";
import type { BlockingEntities } from "./test-account-error";
import type { UserDetail } from "./types";

interface TestAccountDialogProps {
  readonly user: UserDetail | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Receives the value that was just persisted. */
  readonly onSuccess: (isTestAccount: boolean) => void;
}

/**
 * Confirmation step for the ADR-0009 test-partition flag (Amendment 1).
 *
 * This is deliberately a confirm dialog and not a bare switch. The flag is not
 * a display preference: the API re-stamps EVERY property and venue the account
 * already owns in the same transaction, and everything it creates afterwards
 * inherits the flag. The operator gets the consequence — with the exact counts
 * of entities about to move — spelled out before the call goes out.
 */
export function TestAccountDialog({ user, open, onOpenChange, onSuccess }: TestAccountDialogProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [blocking, setBlocking] = useState<BlockingEntities | null>(null);
  const mutation = useSetTestAccount();

  useEffect(() => {
    if (open) {
      setError(null);
      setBlocking(null);
    }
  }, [open, user?.id]);

  if (!user) return null;

  const isTestAccount = user.isTestAccount === true;
  // Toggle semantics: confirming always writes the opposite of current state.
  const nextValue = !isTestAccount;
  const name = user.displayName || user.email;
  // What the cascade will re-stamp. The detail payload carries the owned
  // entities, so the count the operator confirms is the count the API moves.
  const propertyCount = user.ownedProperties.length;
  const venueCount = user.ownedVenues.length;

  const handleOpenChange = (next: boolean) => {
    if (!next && mutation.isPending) return;
    onOpenChange(next);
  };

  const handleConfirm = () => {
    setError(null);
    setBlocking(null);
    mutation.mutate(
      { id: user.id, isTestAccount: nextValue },
      {
        onSuccess: () => {
          onOpenChange(false);
          onSuccess(nextValue);
        },
        onError: (err) => {
          setError(resolveTestAccountError(err, t));
          // Name the entities that blocked it — turns the rule into
          // something the operator can act on. Dialog stays OPEN so the
          // explanation is actually readable.
          if (isBlockingEntitiesError(err)) setBlocking(blockingEntities(err));
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="test-account-dialog">
        <DialogHeader>
          <DialogTitle>
            {nextValue
              ? t("users.testAccount.confirmTitleEnable")
              : t("users.testAccount.confirmTitleDisable")}
          </DialogTitle>
          <DialogDescription>
            {nextValue
              ? t("users.testAccount.confirmEnable", { name })
              : t("users.testAccount.confirmDisable", { name })}
          </DialogDescription>
        </DialogHeader>

        <div
          data-testid="test-account-cascade-note"
          className="flex items-start gap-2 rounded-md border border-violet-500/30 bg-violet-500/10 p-3"
        >
          <Layers className="mt-0.5 h-4 w-4 flex-shrink-0 text-violet-600" aria-hidden="true" />
          <p className="font-sans text-xs font-normal text-violet-900 dark:text-violet-200">
            {nextValue
              ? t("users.testAccount.cascadeNoteEnable", {
                  properties: propertyCount,
                  venues: venueCount,
                })
              : t("users.testAccount.cascadeNoteDisable", {
                  properties: propertyCount,
                  venues: venueCount,
                })}
          </p>
        </div>

        {error && (
          <div
            data-testid="test-account-error"
            className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3"
          >
            <AlertTriangle
              className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive"
              aria-hidden="true"
            />
            <div className="space-y-2">
              <p className="font-sans text-sm font-medium text-destructive">{error}</p>

              {blocking && (blocking.properties.length > 0 || blocking.venues.length > 0) && (
                <div
                  data-testid="test-account-blocking-entities"
                  className="font-sans text-xs text-destructive/90"
                >
                  <p className="font-semibold">{t("users.testAccount.errors.blocking")}</p>
                  {blocking.properties.length > 0 && (
                    <p>
                      {t("users.testAccount.errors.blockingProperties", {
                        names: blocking.properties.join(", "),
                      })}
                    </p>
                  )}
                  {blocking.venues.length > 0 && (
                    <p>
                      {t("users.testAccount.errors.blockingVenues", {
                        names: blocking.venues.join(", "),
                      })}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t("users.actions.cancel")}
          </Button>
          <Button
            data-testid="confirm-test-account-button"
            onClick={handleConfirm}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? t("users.testAccount.pending") : t("users.testAccount.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
